
import { db } from '../firebaseConfig';
// eslint-disable-next-line no-unused-vars
import { collection, addDoc, Timestamp, doc, getDoc, query, where, getDocs, writeBatch } from 'firebase/firestore';

// --- Constants for Default Accounts (Matches satTemplate.js) ---
export const ACCOUNTS = {
    BANCOS: '102.01',
    CLIENTES: '105.01', // Cuotas por Cobrar
    INGRESOS_ORDINARIOS: '401.01',
    CAPITAL_RESERVA: '301.01',
    PROVEEDORES: '201.01' // Cuentas por Pagar (Matches DEFAULT_CHART_OF_ACCOUNTS)
};

/**
 * Creates a standard immutable Ledger Entry (Asiento Contable)
 */
export const postLedgerEntry = async (condominioId, entryData) => {
    // entryData: { fecha, concepto, referenciaId, tipoReferencia, detalles: [{ cuentaId, debe, haber }] }
    try {
        await addDoc(collection(db, 'contabilidad_asientos'), {
            condominioId,
            fecha: entryData.fecha || Timestamp.now(),
            concepto: entryData.concepto,
            referenciaId: entryData.referenciaId,
            tipoReferencia: entryData.tipoReferencia, // 'CARGO', 'PAGO', 'GASTO'
            detalles: entryData.detalles,
            fechaCreacion: Timestamp.now()
        });
        console.log("Asiento contable creado:", entryData.concepto);
    } catch (error) {
        console.error("Error creating ledger entry:", error);
        throw error; // Re-throw to handle in UI
    }
};

/**
 * Helper to get Account ID by Number
 * We need the Firestore Document ID for the 'cuentaId' field in details.
 * This helper fetches the map of 'Number -> DocID'
 */
export const getAccountMap = async (condominioId) => {
    const q = query(collection(db, 'cuentas_contables'), where('condominioId', '==', condominioId));
    const snap = await getDocs(q);
    const map = {}; // '1020' -> 'docId_xyz'
    snap.docs.forEach(d => {
        const data = d.data();
        map[data.numero] = d.id;
    });
    return map;
};

/**
 * Rebuilds the entire Ledger from:
 * 1. Cargos (Charges) -> Debit Clients / Credit Income
 * 2. Movimientos Bancarios (Ingresos Conciliados) -> Debit Bank / Credit Clients
 * 3. Gastos (Clasificados) -> Debit ExpenseCategory / Credit Bank
 */
export const syncLedgerHistory = async (condominioId) => {
    console.log("Starting Ledger Sync for:", condominioId);

    // 1. Fetch Account Map
    const accountMap = await getAccountMap(condominioId);
    console.log("Account Map Loaded", Object.keys(accountMap).length);

    if (!accountMap[ACCOUNTS.BANCOS] || !accountMap[ACCOUNTS.CLIENTES] || !accountMap[ACCOUNTS.INGRESOS_ORDINARIOS]) {
        throw new Error("Faltan cuentas clave en el Plan de Cuentas (Bancos, Clientes, Ingresos). Ejecuta 'Inicializar' en Plan de Cuentas primero.");
    }

    const batchSize = 400; // Firestore batch limit is 500
    let batch = writeBatch(db);
    let operationCount = 0;

    // Helper to commit and reset batch
    const checkBatch = async () => {
        operationCount++;
        if (operationCount >= batchSize) {
            await batch.commit();
            batch = writeBatch(db);
            operationCount = 0;
        }
    };

    // --- STEP 0: CLEAR EXISTING ENTRIES ---
    // Prevent duplication by wiping the slate clean for this condo before rebuilding.
    console.log("Clearing existing ledger entries...");
    const qClear = query(collection(db, 'contabilidad_asientos'), where('condominioId', '==', condominioId));
    const snapClear = await getDocs(qClear);

    // Keep manual entries and closing entries
    const toDeleteDocs = snapClear.docs.filter(docSnap => {
        const data = docSnap.data();
        return data.source !== 'Manual' && data.tipoReferencia !== 'CIERRE';
    });

    const chunkArray = (arr, size) => {
        const res = [];
        for (let i = 0; i < arr.length; i += size) res.push(arr.slice(i, i + size));
        return res;
    }

    // Delete in batches of 400
    const chunks = chunkArray(toDeleteDocs, 400);
    for (const chunk of chunks) {
        const deleteBatch = writeBatch(db);
        chunk.forEach(docSnap => deleteBatch.delete(docSnap.ref));
        await deleteBatch.commit();
    }
    console.log(`Cleared ${toDeleteDocs.length} automated entries (preserved manual & closing entries).`);

    // Rule: Debit 1050 (Clients), Credit 4010 (Income)
    const qCargos = query(collection(db, 'cargos'), where('condominioId', '==', condominioId));
    const snapCargos = await getDocs(qCargos);

    for (const docCargo of snapCargos.docs) {
        const cargo = docCargo.data();

        // --- FILTER MARKETPLACE ---
        if (cargo.origen === 'marketplace' || cargo.tipo === 'Servicio Marketplace') {
            continue;
        }

        // --- FILTER CANCELLED ---
        const isCancelled = cargo.estatus && (cargo.estatus.toLowerCase() === 'cancelado' || cargo.estatus.toLowerCase() === 'cancelada');
        if (isCancelled) {
            continue;
        }

        const asientoRef = doc(collection(db, 'contabilidad_asientos'));

        // Income Account: Map to the correct account depending on type (SAT Dotted standard)
        let incomeAccountKey = ACCOUNTS.INGRESOS_ORDINARIOS;
        if (cargo.tipo === 'Cuota Extraordinaria' && accountMap['401.02']) {
            incomeAccountKey = accountMap['401.02']; // Resolve dynamic docId
        } else if (cargo.tipo && (cargo.tipo.includes('Multa') || cargo.tipo.includes('Recargo') || cargo.tipo.includes('Interés')) && accountMap['403.01']) {
            incomeAccountKey = accountMap['403.01']; // Resolve dynamic docId
        } else {
            incomeAccountKey = accountMap[ACCOUNTS.INGRESOS_ORDINARIOS]; // Resolve dynamic docId
        }

        batch.set(asientoRef, {
            condominioId,
            fecha: cargo.fechaCreacion || Timestamp.now(), // Use creation date for accrual
            concepto: `Provisión Cargo: ${cargo.descripcion || cargo.concepto || 'Cargo'}`,
            referenciaId: docCargo.id,
            tipoReferencia: 'CARGO',
            detalles: [
                { cuentaId: accountMap[ACCOUNTS.CLIENTES], debe: parseFloat(cargo.monto), haber: 0 },
                { cuentaId: incomeAccountKey, debe: 0, haber: parseFloat(cargo.monto) }
            ],
            metadata: {
                unidad: cargo.unidadResidente || cargo.unidad || '',
                residenteNombre: cargo.nombreResidente || cargo.residenteNombre || '',
                referenciaNombre: (cargo.nombreResidente || cargo.residenteNombre) ? `${cargo.unidadResidente || cargo.unidad || ''} - ${cargo.nombreResidente || cargo.residenteNombre}` : cargo.unidadResidente || cargo.unidad || ''
            },
            fechaCreacion: Timestamp.now()
        });
        await checkBatch();
    }

    // --- STEP 2: Process PAYMENTS (Collection) ---
    // Rule: Debit 1020 (Bank), Credit 1050 (Clients)
    const qMovs = query(
        collection(db, 'movimientos_bancarios'),
        where('condominioId', '==', condominioId),
        where('estatus', '==', 'conciliado') // Only reconciled income
        // Note: 'identificado' might not be fully treated as paid? 
        // Logic: 'conciliado' means applied to debt.
        // What about 'Saldo a Favor' generation?
        // If it generated Saldo a Favor, the Credit should be to 'Anticipos de Clientes' (Liability)?
        // For MVP simplicity: Credit 'Clientes' (1050) reducing the balance. 
        // If they overpay, Clientes goes negative (which is technically an asset acting as liab). acceptable for v1.
    );
    const snapMovs = await getDocs(qMovs);

    for (const docMov of snapMovs.docs) {
        const mov = docMov.data();
        // Skip Stripe movements to avoid double-posting split entries
        if (mov.source === 'stripe') {
            continue;
        }
        // Only process POSITIVE amounts here (Ingresos)
        if (mov.monto > 0) {
            const asientoRef = doc(collection(db, 'contabilidad_asientos'));
            batch.set(asientoRef, {
                condominioId,
                fecha: mov.fecha || Timestamp.now(),
                concepto: `Cobro: ${mov.descripcion}`,
                referenciaId: docMov.id,
                tipoReferencia: 'PAGO',
                detalles: [
                    { cuentaId: accountMap[ACCOUNTS.BANCOS], debe: parseFloat(mov.monto), haber: 0 },
                    { cuentaId: accountMap[ACCOUNTS.CLIENTES], debe: 0, haber: parseFloat(mov.monto) }
                ],
                metadata: {
                    unidad: mov.unidad || '',
                    residenteNombre: mov.residenteNombre || '',
                    referenciaNombre: mov.residenteNombre ? `${mov.unidad} - ${mov.residenteNombre}` : mov.unidad || '',
                    descripcionOriginal: mov.descripcion || ''
                },
                fechaCreacion: Timestamp.now()
            });
            await checkBatch();
        }
    }

    // --- STEP 2.5: Optimize Movement Map for Verification ---
    // We need to verify orphaned expenses. Let's fetch ALL movements for the condo to check links.
    // Optimization: Only fetch if we didn't already (we fetched 'conciliado' in step 2).
    // Let's just fetch id & gastoId for all movements to be safe and fast.
    const qAllMovs = query(collection(db, 'movimientos_bancarios'), where('condominioId', '==', condominioId));
    const snapAllMovs = await getDocs(qAllMovs);
    const movMap = {}; // movId -> gastoId
    snapAllMovs.docs.forEach(d => {
        const m = d.data();
        movMap[d.id] = m.gastoId;
    });

    // --- STEP 3: Process EXPENSES (Gastos) ---
    // Rule: Debit ExpenseCategory, Credit 1020 (Bank)
    const qGastos = query(collection(db, 'gastos'), where('condominioId', '==', condominioId));
    const snapGastos = await getDocs(qGastos);

    for (const docGasto of snapGastos.docs) {
        const gasto = docGasto.data();

        // ORPHAN CHECK:
        // If this expense is linked to a bank movement, verify that the movement still points to THIS expense.
        // If not, it means this expense was "undone" and is now trash.
        if (gasto.movimientoBancarioId) {
            const activeGastoId = movMap[gasto.movimientoBancarioId];
            if (activeGastoId !== docGasto.id) {
                console.log(`Skipping orphaned expense: ${docGasto.id} (Mov points to ${activeGastoId})`);
                continue;
            }
        }

        const asientoRef = doc(collection(db, 'contabilidad_asientos'));

        // gasto.cuentaContableId ALREADY holds the correct Firestore Doc ID for the account!
        // (Because ClasificarGastoModal saves it)
        const expenseAccountId = gasto.cuentaContableId;

        if (expenseAccountId) {
            // Determine Direction based on 'gasto.tipo' ('ingreso' or 'egreso')
            const isIncome = gasto.tipo === 'ingreso';

            let detalles = [];
            if (isIncome) {
                // INCOME: Debit Bank, Credit Account
                detalles = [
                    { cuentaId: accountMap[ACCOUNTS.BANCOS], debe: parseFloat(gasto.monto), haber: 0 },
                    { cuentaId: expenseAccountId, debe: 0, haber: parseFloat(gasto.monto) }
                ];
            } else {
                // EXPENSE: Debit Account, Credit Bank
                detalles = [
                    { cuentaId: expenseAccountId, debe: parseFloat(gasto.monto), haber: 0 },
                    { cuentaId: accountMap[ACCOUNTS.BANCOS], debe: 0, haber: parseFloat(gasto.monto) }
                ];
            }

            batch.set(asientoRef, {
                condominioId,
                fecha: gasto.fecha || Timestamp.now(),
                concepto: `Clasificado: ${gasto.descripcion || gasto.categoria}`,
                referenciaId: docGasto.id,
                tipoReferencia: isIncome ? 'INGRESO_CLASIFICADO' : 'GASTO',
                detalles: detalles,
                metadata: {
                    proveedor: gasto.proveedor || '',
                    referenciaNombre: gasto.proveedor || ''
                },
                fechaCreacion: Timestamp.now()
            });
            await checkBatch();
        }
    }

    // Final Commit
    if (operationCount > 0) {
        await batch.commit();
    }

    console.log("Sync Complete");
    return true;
};

/**
 * Calculates progressive Mexican ISR based on config_sat_tablas
 */
export const calculateSATProgressiveISR = async (sueldoBruto, periodType) => {
    try {
        const docRef = doc(db, 'config_sat_tablas', periodType || 'Quincenal');
        const snap = await getDoc(docRef);
        if (!snap.exists()) {
            console.warn(`SAT tables not seeded for period ${periodType}. Using 10% flat rate fallback.`);
            return parseFloat((sueldoBruto * 0.10).toFixed(2));
        }

        const rangos = snap.data().rangos || [];
        const bracket = rangos.find(r => sueldoBruto >= r.limInf && sueldoBruto <= r.limSup) || rangos[rangos.length - 1];
        if (!bracket) return 0;

        const excedente = sueldoBruto - bracket.limInf;
        const impuestoExcedente = excedente * (bracket.pct / 100);
        const totalISR = bracket.cuotaFija + impuestoExcedente;

        return parseFloat(totalISR.toFixed(2));
    } catch (e) {
        console.error("Error calculating progressive ISR:", e);
        return parseFloat((sueldoBruto * 0.10).toFixed(2));
    }
};

/**
 * Creates the ledger entries for payroll devengo (Stamping)
 */
export const postPayrollDevengoAsiento = async (condominioId, reciboId, data) => {
    const { trabajadorNombre, periodo, sueldoBruto, netoPagado, isrRetenido, imssRetenido, imssPatronal, isn } = data;
    try {
        const accountMap = await getAccountMap(condominioId);
        
        const accGasto = accountMap['601.01'];
        const accPasivoSueldos = accountMap['210.01'];
        const accPasivoImss = accountMap['211.01'];
        const accPasivoIsn = accountMap['213.07'];
        const accPasivoIsr = accountMap['216.01'];

        if (!accGasto || !accPasivoSueldos) {
            console.error("Missing standard chart of accounts. Cannot generate payroll ledger entry.");
            return;
        }

        const totalGasto = sueldoBruto + imssPatronal + isn;

        const detalles = [
            { cuentaId: accGasto, debe: parseFloat(totalGasto.toFixed(2)), haber: 0 },
            { cuentaId: accPasivoSueldos, debe: 0, haber: parseFloat(netoPagado.toFixed(2)) },
            { cuentaId: accPasivoIsr, debe: 0, haber: parseFloat(isrRetenido.toFixed(2)) },
            { cuentaId: accPasivoImss, debe: 0, haber: parseFloat((imssRetenido + imssPatronal).toFixed(2)) },
            { cuentaId: accPasivoIsn, debe: 0, haber: parseFloat(isn.toFixed(2)) }
        ];

        await postLedgerEntry(condominioId, {
            fecha: Timestamp.now(),
            concepto: `Provisión Nómina: ${trabajadorNombre} - ${periodo}`,
            referenciaId: reciboId,
            tipoReferencia: 'NOMINA_DEVENGO',
            detalles
        });
    } catch (e) {
        console.error("Error posting payroll devengo ledger entry:", e);
    }
};

/**
 * Creates the ledger entries for payroll payment (Dispersion)
 */
export const postPayrollPagoAsiento = async (condominioId, reciboId, data) => {
    const { trabajadorNombre, periodo, netoPagado } = data;
    try {
        const accountMap = await getAccountMap(condominioId);
        const accPasivoSueldos = accountMap['210.01'];
        const accBancos = accountMap['102.01'];

        if (!accPasivoSueldos || !accBancos) {
            console.error("Missing standard chart of accounts. Cannot generate payroll payment ledger entry.");
            return;
        }

        const detalles = [
            { cuentaId: accPasivoSueldos, debe: parseFloat(netoPagado.toFixed(2)), haber: 0 },
            { cuentaId: accBancos, debe: 0, haber: parseFloat(netoPagado.toFixed(2)) }
        ];

        await postLedgerEntry(condominioId, {
            fecha: Timestamp.now(),
            concepto: `Pago Nómina: ${trabajadorNombre} - ${periodo}`,
            referenciaId: reciboId,
            tipoReferencia: 'NOMINA_PAGO',
            detalles
        });
    } catch (e) {
        console.error("Error posting payroll payment ledger entry:", e);
    }
};
